import React from 'react';
import { Link } from 'react-router-dom';

const MSCounseling = () => {
  const courses = [
    { code: 'GCS 503', name: 'Survey of Research in Human Development and Behavior', credits: 3 },
    { code: 'GCS 506', name: 'Survey of Research Methodology', credits: 3 },
    { code: 'GCS 508', name: 'Professional and Scientific Ethics', credits: 3 },
    { code: 'GCS 510', name: 'Scope of Human Services', credits: 3 },
    { code: 'GCS 514', name: 'Foundations of Addictive and Compulsive Behavior', credits: 3 },
    { code: 'GCS 517', name: 'Theories of Personality', credits: 3 },
    { code: 'GCS 531', name: 'Mental Health Counseling', credits: 3 },
    { code: 'GCS 533', name: 'Counseling and Guidance in Diverse Populations', credits: 3 },
    { code: 'GCS 535', name: 'Counseling Studies Integrative Project', credits: 6 },
    { code: 'GCS 586', name: 'Internship', credits: 6 },
    { code: 'GCS 598', name: 'Comprehensive Examination', credits: 3 }
  ];

  const programFeatures = [
    {
      title: "Research-Based Approach",
      description: "Strong foundation in human development and research methodology",
      icon: "📚"
    },
    {
      title: "Practical Experience",
      description: "Extensive internship opportunities and hands-on training",
      icon: "💼"
    },
    {
      title: "Diverse Populations",
      description: "Specialized training in counseling across different cultural contexts",
      icon: "🌍"
    },
    {
      title: "Mental Health Focus",
      description: "Comprehensive training in mental health counseling and addiction",
      icon: "🧠"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Master of Science in General Counseling
            </h1>
            <p className="text-xl opacity-90">
              Comprehensive training for professional counseling practice
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <div className="prose max-w-none text-gray-600">
                <p className="mb-4">
                  The Master of Science in General Counseling program is designed for professionals 
                  seeking comprehensive training in counseling practice. Our program encompasses 39 
                  credit hours of practical learning in a format that accommodates full-time workers.
                </p>
                <p>
                  Students complete their coursework through a combination of online learning, evening 
                  classes, and supervised field experiences, preparing them for diverse counseling roles 
                  across various settings.
                </p>
              </div>
            </div>
          </section>

          {/* Program Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {programFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Curriculum Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Curriculum</h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="space-y-6">
                {courses.map((course, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-16 h-16 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center text-sm font-medium">
                      {course.credits} CR
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{course.code}</h3>
                      <p className="text-gray-600">{course.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Career Opportunities Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Career Opportunities</h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-primary-600 mb-2">Mental Health Counseling</h3>
                  <p className="text-gray-600">Work in mental health clinics, private practice, or healthcare settings</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-primary-600 mb-2">Addiction Counseling</h3>
                  <p className="text-gray-600">Specialize in substance abuse and behavioral disorders treatment</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-primary-600 mb-2">Community Services</h3>
                  <p className="text-gray-600">Work with community organizations and social service agencies</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-primary-600 mb-2">Healthcare Settings</h3>
                  <p className="text-gray-600">Provide counseling services in hospitals and healthcare facilities</p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Start Your Counseling Career</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Take the first step towards becoming a professional counselor and making a 
                difference in people's lives.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link
                  to="/application"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
                >
                  Apply Now
                </Link>
                <Link
                  to="/request"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-primary-700 bg-white border border-primary-200 hover:bg-primary-50 transition-colors duration-200"
                >
                  Request Information
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MSCounseling; 